import { logger } from '@/config/logging';
import { describeAuthState } from '@/lib/authDiagnostics';

const baseUrl = `${window.location.protocol}//${window.location.host}`;

// #3577: failed API responses, with the auth state at that moment. Lives in
// the auth category so it lands in the persisted auth log (`serv.authLog()`).
const responseLogger = logger.getChild(['auth', 'api']);

/** `Error` thrown by `verifyResponse`, carrying what the message may lack. */
export interface ApiResponseError extends Error {
  status: number;
  url: string;
}

export function resolveUrl(
  path: string,
  qry?: Record<string, string | number | boolean>,
) {
  const strQry =
    qry &&
    Object.fromEntries(Object.entries(qry).map(([k, v]) => [k, v.toString()]));
  const queryParams = new URLSearchParams(strQry);
  const url = new URL(`/api${path}`, baseUrl);
  url.search = queryParams.toString();
  return url.toString();
}

/** Check if a response is ok, throw an exception otherwise */
export async function verifyResponse(response: Response) {
  if (!response.ok) {
    let message;
    let bodyExcerpt: string | undefined;
    let bodyIsJson = false;
    try {
      const text = await response.text();
      bodyExcerpt = text.slice(0, 300);
      const { error, message: msg } = JSON.parse(text); // postgrest http errors use 'message' field
      bodyIsJson = true;
      message = error || msg;
    } catch {
      // Not JSON (e.g. express `sendStatus(401)` → "Unauthorized", or a proxy
      // error page). Over HTTP/2 `statusText` is empty, so the Error below can
      // end up with no message at all — the panel then shows just "Error".
      message = response.statusText;
    }

    const url = new URL(response.url || baseUrl);
    // 401/403 after idle is the #3577 trace; everything else is context.
    const level =
      response.status === 401 || response.status === 403 ? 'warn' : 'info';
    responseLogger[level]('API {path} failed: {status}', {
      path: url.pathname + url.search,
      status: response.status,
      statusText: response.statusText || null,
      contentType: response.headers.get('content-type'),
      degraded: response.headers.get('x-sw-degraded'),
      bodyIsJson,
      bodyExcerpt,
      messageShown: message || '(empty)',
      onLine: navigator.onLine,
      visibility: document.visibilityState,
      msSinceLoad: Math.round(performance.now()),
      auth: describeAuthState(),
    });

    const error = new Error(message) as ApiResponseError;
    error.status = response.status;
    error.url = response.url;
    throw error;
  }
  return response;
}
export function first<T>(arr: T[]): T {
  if (!arr.length) {
    throw new Error('Expected first object');
  }
  return arr[0];
}
